<script setup lang="ts">
import type SwiperClass from "swiper";
import SwiperCore from "swiper";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

SwiperCore.use([Autoplay, EffectFade, Navigation, Pagination]);

const nuxtApp = useNuxtApp();
const page = usePage();
const { gsap, loadGSAP } = useGSAP();
const autoplayEnabled = ref(false);
const swiperInstance = ref<SwiperClass | null>(null);
const activeIndex = ref(0);
const windowWidth = ref(0);
const isMobile = ref(false);
const videoReady = ref(false);

type KirbyImage = {
  url?: string;
  width?: number;
  height?: number;
  alt?: string;
};

type VideoSlide = {
  videoSlideTitle?: string;
  videoSlideSubTitle?: string;
  ctaLink?: string;
  vimeoSlideUrl?: string;
  vimeoSlideUrlMobile?: string;
  desktopPoster?: KirbyImage | string | null;
  mobilePoster?: KirbyImage | string | null;
};

function posterUrl(poster: KirbyImage | string | null | undefined): string {
  if (!poster) return "";
  if (typeof poster === "string") return poster;
  return poster.url || "";
}

const videoSlides = computed<VideoSlide[]>(() => {
  const slides = (page.value?.videoSlides || []) as VideoSlide[];
  if (isMobile.value && slides.length) {
    return slides.slice(0, 6);
  }
  return slides;
});

const firstSlide = computed(() => {
  const slides = (page.value?.videoSlides || []) as VideoSlide[];
  return slides[0] || null;
});

const img = useImage();

const firstPosterRaw = computed(() => {
  const slide = firstSlide.value;
  // Prefer desktop for the default <img>; mobile via <source media>
  return (
    posterUrl(slide?.desktopPoster || slide?.mobilePoster) ||
    posterUrl(page.value?.heroVideoPoster) ||
    ""
  );
});

const firstPosterMobileRaw = computed(() => {
  const slide = firstSlide.value;
  return (
    posterUrl(slide?.mobilePoster || slide?.desktopPoster) ||
    posterUrl(page.value?.heroVideoPoster) ||
    firstPosterRaw.value
  );
});

const firstPosterWebp = computed(() => {
  if (!firstPosterRaw.value) return "";
  return img(firstPosterRaw.value, { width: 1780, format: "webp", quality: 75 });
});

const firstPosterMobileWebp = computed(() => {
  if (!firstPosterMobileRaw.value) return "";
  return img(firstPosterMobileRaw.value, {
    width: 900,
    format: "webp",
    quality: 75,
  });
});

const firstPosterAlt = computed(() =>
  firstSlide.value?.videoSlideTitle
    ? `Hero: ${firstSlide.value.videoSlideTitle}`
    : "Mont Blanc Treks hero",
);

useHead({
  link: computed(() => {
    const links: Array<Record<string, string>> = [];
    if (firstPosterMobileWebp.value) {
      links.push({
        rel: "preload",
        as: "image",
        href: firstPosterMobileWebp.value,
        media: "(max-width: 991px)",
        fetchpriority: "high",
      });
    }
    if (firstPosterWebp.value) {
      links.push({
        rel: "preload",
        as: "image",
        href: firstPosterWebp.value,
        media: "(min-width: 992px)",
        fetchpriority: "high",
      });
    }
    return links;
  }),
});

function pauseAllVideos() {
  const allVideos = document.querySelectorAll("#homeHero video, .main-hero video");
  allVideos.forEach((video) => {
    (video as HTMLVideoElement).pause();
  });
}

function playVideoEl(video: HTMLVideoElement) {
  video.muted = true;
  // Ensure the source is loading so play isn't waiting on preload=none
  if (video.readyState < 2) {
    video.load();
  }
  const tryPlay = () => {
    video.play().catch((error) => {
      console.error("Autoplay failed:", error);
    });
  };
  if (video.readyState >= 2) {
    tryPlay();
  } else {
    video.addEventListener("loadeddata", tryPlay, { once: true });
  }
}

function playActiveSlideVideo() {
  if (!heroInView.value || !swiperInstance.value) return;
  const currentSlide =
    swiperInstance.value.slides[swiperInstance.value.activeIndex];
  if (!currentSlide) return;
  const videos = currentSlide.querySelectorAll(
    "video",
  ) as NodeListOf<HTMLVideoElement>;
  videos.forEach((video) => playVideoEl(video));
}

/** Warm the next slide's video so the fade doesn't sit on a still poster. */
function prefetchAdjacentVideos(swiper: SwiperClass) {
  const indexes = [swiper.activeIndex + 1, swiper.activeIndex - 1];
  for (const i of indexes) {
    const slide = swiper.slides[i];
    if (!slide) continue;
    slide.querySelectorAll("video").forEach((video) => {
      const el = video as HTMLVideoElement;
      if (el.readyState < 2) el.load();
    });
  }
}

function onSlideChangeTransitionStart(swiper: SwiperClass) {
  pauseAllVideos();
  activeIndex.value = swiper.activeIndex;
  if (!heroInView.value) return;
  const nextSlide = swiper.slides[swiper.activeIndex];
  if (!nextSlide) return;
  nextSlide.querySelectorAll("video").forEach((video) => {
    const el = video as HTMLVideoElement;
    el.currentTime = 0;
    playVideoEl(el);
  });
  prefetchAdjacentVideos(swiper);
}

function onSlideChangeTransitionEnd(swiper: SwiperClass) {
  activeIndex.value = swiper.activeIndex;
  if (!heroInView.value) return;
  // Keep active playing if the start handler already kicked it off
  playActiveSlideVideo();
  prefetchAdjacentVideos(swiper);
}

const heroRoot = ref<HTMLElement | null>(null);
const heroInView = ref(true);
let heroObserver: IntersectionObserver | null = null;

function resolveSlidePoster(slide: VideoSlide | null | undefined): string {
  return (
    posterUrl(slide?.desktopPoster || slide?.mobilePoster) ||
    posterUrl(page.value?.heroVideoPoster) ||
    ""
  );
}

function resolveSlidePosterMobile(slide: VideoSlide | null | undefined): string {
  return (
    posterUrl(slide?.mobilePoster || slide?.desktopPoster) ||
    posterUrl(page.value?.heroVideoPoster) ||
    ""
  );
}

function markFirstVideoReady() {
  videoReady.value = true;
  autoplayEnabled.value = true;
  if (swiperInstance.value?.autoplay?.start) {
    swiperInstance.value.autoplay.start();
  }
  playActiveSlideVideo();
  if (swiperInstance.value) prefetchAdjacentVideos(swiperInstance.value);
}

function checkFirstSlideVideoLoaded() {
  const firstSlideEl = document.querySelector(".main-hero .swiper-slide");
  if (!firstSlideEl) return;
  const firstVideo = firstSlideEl.querySelector("video") as HTMLVideoElement | null;
  if (!firstVideo) return;

  // Start buffering immediately (preload was none for LCP)
  firstVideo.load();
  playVideoEl(firstVideo);

  if (firstVideo.readyState >= 3) {
    markFirstVideoReady();
    return;
  }

  firstVideo.addEventListener(
    "canplaythrough",
    () => {
      markFirstVideoReady();
    },
    { once: true },
  );
  // Don't hold the slider forever if the stream is slow
  setTimeout(() => {
    if (!videoReady.value) markFirstVideoReady();
  }, 2500);
}

function initializeSwiper(swiper: SwiperClass) {
  swiperInstance.value = swiper;
  if (!autoplayEnabled.value && swiper.autoplay) {
    swiper.autoplay.stop();
  }
  // After Swiper mounts, wire first-video LCP handoff
  requestAnimationFrame(() => checkFirstSlideVideoLoaded());
}

watch(autoplayEnabled, (enabled) => {
  if (!swiperInstance.value?.autoplay) return;
  if (enabled) {
    swiperInstance.value.autoplay.start();
  } else {
    swiperInstance.value.autoplay.stop();
  }
});

nuxtApp.hook("page:finish", () => {
  checkFirstSlideVideoLoaded();
});

function updateWidth() {
  isMobile.value = window.innerWidth <= 991;
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  // Prefer matchMedia so first client paint matches SSR desktop poster until resize.
  if (typeof window !== "undefined" && window.matchMedia) {
    isMobile.value = window.matchMedia("(max-width: 991px)").matches;
  } else {
    isMobile.value = window.innerWidth <= 991;
  }
  windowWidth.value = window.innerWidth;
  updateWidth();
  window.addEventListener("resize", updateWidth);

  if (typeof IntersectionObserver !== "undefined" && heroRoot.value) {
    heroObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        heroInView.value = !!entry?.isIntersecting;
        if (heroInView.value) {
          playActiveSlideVideo();
        } else {
          pauseAllVideos();
        }
      },
      { threshold: 0.25 },
    );
    heroObserver.observe(heroRoot.value);
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
  heroObserver?.disconnect();
  heroObserver = null;
});

const scrollToIntro = async () => {
  await loadGSAP();
  if (gsap.value) {
    gsap.value.to(window, {
      duration: 1,
      scrollTo: {
        y: "#homeIntro",
        offsetY: 100,
      },
      ease: "power2.inOut",
    });
  } else {
    document.getElementById("homeIntro")?.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<template>
  <div
    ref="heroRoot"
    class="main-hero hero"
    :aria-label="
      firstSlide?.videoSlideTitle
        ? `Hero video: ${firstSlide.videoSlideTitle}`
        : 'Mont Blanc Treks hero video'
    "
  >
    <div class="main-hero-slider slider-video">
      <AppSVGBrandGraphic />
      <div class="chevron-container" @click.prevent="scrollToIntro">
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
      </div>
      <AppSVGBrandGraphic2 />

      <!-- SSR LCP image — stays in DOM (fades out) so discovery + fetchpriority apply -->
      <picture
        v-if="firstPosterRaw"
        class="hero-lcp-poster"
        :class="{ 'is-hidden': videoReady }"
      >
        <source
          v-if="firstPosterMobileWebp"
          media="(max-width: 991px)"
          :srcset="firstPosterMobileWebp"
          type="image/webp"
        />
        <source
          v-if="firstPosterWebp"
          media="(min-width: 992px)"
          :srcset="firstPosterWebp"
          type="image/webp"
        />
        <img
          :src="firstPosterMobileWebp || firstPosterMobileRaw || firstPosterRaw"
          :alt="firstPosterAlt"
          width="1200"
          height="675"
          fetchpriority="high"
          loading="eager"
          decoding="async"
        />
      </picture>

      <!-- Solid brand-blue shows until poster/video is ready -->

      <Transition name="fadeimg" appear>
        <div v-if="page?.videoSlides?.length || page?.slides">
          <ClientOnly>
            <Swiper
              :slides-per-view="1"
              :loop="false"
              :pagination="false"
              :navigation="true"
              effect="fade"
              :modules="[Autoplay, EffectFade, Navigation, Pagination]"
              :autoplay="{ delay: 5000, disableOnInteraction: false }"
              :allow-touch-move="autoplayEnabled"
              @swiper="initializeSwiper"
              @slide-change-transition-start="onSlideChangeTransitionStart"
              @slide-change-transition-end="onSlideChangeTransitionEnd"
            >
              <SwiperSlide
                v-for="(slide, index) in videoSlides"
                :key="index"
                class="swiper-slide"
              >
                <div class="slider-wrap">
                  <AppHeroCta />
                  <div class="page-title-wrapper">
                    <div class="page-title">
                      <!-- First slide title: no appear animation (show immediately) -->
                      <h2 v-if="index === activeIndex && index === 0">
                        {{ slide.videoSlideTitle }}
                      </h2>
                      <Transition
                        v-else-if="index === activeIndex"
                        name="slide-left"
                        appear
                      >
                        <h2>{{ slide.videoSlideTitle }}</h2>
                      </Transition>
                      <Transition
                        v-if="index === activeIndex"
                        name="slide-up"
                        appear
                      >
                        <h3>{{ slide.videoSlideSubTitle }}</h3>
                      </Transition>
                      <Transition
                        v-if="index === activeIndex"
                        name="slide-left"
                        appear
                      >
                        <NuxtLink
                          v-if="page?.template === 'home'"
                          class="hero-cta-btn btn from-left"
                          :to="slide?.ctaLink?.trim()"
                        >
                          View {{ slide.videoSlideTitle }}
                        </NuxtLink>
                      </Transition>
                    </div>
                  </div>
                  <div class="hero-fade"></div>
                  <div class="vimeo-wrapper">
                    <!--
                      First slide: no poster attr — SSR .hero-lcp-poster is the LCP
                      image (discoverable in HTML with fetchpriority=high).
                    -->
                    <video
                      v-if="!isMobile"
                      playsinline
                      muted
                      loop
                      :preload="
                        index === 0
                          ? 'none'
                          : index === activeIndex + 1
                            ? 'auto'
                            : 'metadata'
                      "
                      :poster="index === 0 ? undefined : resolveSlidePoster(slide)"
                      :aria-label="
                        slide.videoSlideTitle
                          ? `Video: ${slide.videoSlideTitle}`
                          : 'Hero trek video'
                      "
                    >
                      <source :src="slide?.vimeoSlideUrl" type="video/mp4" />
                    </video>

                    <video
                      v-if="isMobile"
                      playsinline
                      muted
                      loop
                      :preload="
                        index === 0
                          ? 'none'
                          : index === activeIndex + 1
                            ? 'auto'
                            : 'metadata'
                      "
                      :poster="
                        index === 0 ? undefined : resolveSlidePosterMobile(slide)
                      "
                      class="video-fade-in"
                      :aria-label="
                        slide.videoSlideTitle
                          ? `Video: ${slide.videoSlideTitle}`
                          : 'Hero trek video'
                      "
                    >
                      <source
                        :src="slide?.vimeoSlideUrlMobile || slide?.vimeoSlideUrl"
                        type="video/mp4"
                      />
                    </video>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </ClientOnly>
          <!-- SSR fallback titles so first paint isn't empty (hidden once Swiper mounts) -->
          <div
            v-if="firstSlide && !swiperInstance"
            class="page-title-wrapper hero-ssr-titles"
          >
            <div class="page-title">
              <h2>{{ firstSlide.videoSlideTitle }}</h2>
              <h3>{{ firstSlide.videoSlideSubTitle }}</h3>
            </div>
          </div>
        </div>
      </Transition>
    </div>
    <AppAnnouncement />
  </div>
</template>
